import { useCallback, useRef, useState } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { expValEquals } from '@confluence/feature-experiments';

import { APPROVALS_ANALYTICS_SOURCE } from './analytics.ts';

type UseLiveDocConversionBlockedModalArgs = {
	contentId: string;
	contentType: string | null;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	disallowFirstPublish: boolean | undefined;
	isLivePage: boolean;
};

export const useLiveDocConversionBlockedModal = ({
	contentId,
	contentType,
	createAnalyticsEvent,
	disallowFirstPublish,
	isLivePage,
}: UseLiveDocConversionBlockedModalArgs) => {
	const [isLiveDocConversionBlockedModalOpen, setIsLiveDocConversionBlockedModalOpen] =
		useState(false);
	const isLiveDocConversionBlockedModalOpenRef = useRef(false);

	const shouldBlockLiveDocConversion =
		isLivePage &&
		disallowFirstPublish === true &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
		expValEquals('confluence_approvals_live_doc_conversion_block_exp', 'isEnabled', true);

	const handleCloseLiveDocConversionBlockedModal = useCallback(() => {
		isLiveDocConversionBlockedModalOpenRef.current = false;
		setIsLiveDocConversionBlockedModalOpen(false);
	}, []);

	const handleLiveDocConversionBlockedByApproval = useCallback(() => {
		if (!isLiveDocConversionBlockedModalOpenRef.current) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'liveDocConversionBlockedScreen',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: {
						contentId,
						contentType,
						isLivePage,
						conversionSource: 'liveDocToPage',
					},
				},
			}).fire();
		}

		isLiveDocConversionBlockedModalOpenRef.current = true;
		setIsLiveDocConversionBlockedModalOpen(true);
	}, [contentId, contentType, createAnalyticsEvent, isLivePage]);

	return {
		handleCloseLiveDocConversionBlockedModal,
		handleLiveDocConversionBlockedByApproval,
		isLiveDocConversionBlockedModalOpen,
		shouldBlockLiveDocConversion,
	};
};
